import React, { useState, useEffect } from 'react';
import {
  Shield,
  Lock,
  Unlock,
  Key,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Activity,
  Settings,
  RefreshCw,
  Save,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Server,
  Database,
  Network,
  Zap,
  Bell,
  Mail,
  Phone
} from 'lucide-react';

interface SecurityEvent {
  id: string;
  timestamp: string;
  type: 'login_attempt' | 'permission_change' | 'data_access' | 'system_breach' | 'suspicious_activity';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  source: string;
  ipAddress: string;
  userId?: string;
  status: 'resolved' | 'investigating' | 'open';
  details: any;
}

interface SecuritySettings {
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    maxAge: number;
  };
  sessionSettings: {
    maxDuration: number;
    idleTimeout: number;
    maxConcurrentSessions: number;
  };
  accessControl: {
    enableTwoFactor: boolean;
    enableIpWhitelist: boolean;
    enableRateLimit: boolean;
    maxLoginAttempts: number;
    lockoutDuration: number;
  };
  monitoring: {
    enableAuditLog: boolean;
    enableRealTimeAlerts: boolean;
    alertEmail: string;
    alertPhone: string;
  };
}

interface SecurityStats {
  totalEvents: number;
  criticalEvents: number;
  resolvedEvents: number;
  activeThreats: number;
}

const SecurityPage: React.FC = () => {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [settings, setSettings] = useState<SecuritySettings>({
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
      maxAge: 90
    },
    sessionSettings: {
      maxDuration: 480, // 8 hours
      idleTimeout: 30,
      maxConcurrentSessions: 3
    },
    accessControl: {
      enableTwoFactor: true,
      enableIpWhitelist: false,
      enableRateLimit: true,
      maxLoginAttempts: 5,
      lockoutDuration: 15
    },
    monitoring: {
      enableAuditLog: true,
      enableRealTimeAlerts: true,
      alertEmail: 'admin@voltron.network',
      alertPhone: '+1-555-0123'
    }
  });
  const [stats, setStats] = useState<SecurityStats>({
    totalEvents: 0,
    criticalEvents: 0,
    resolvedEvents: 0,
    activeThreats: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 20;

  useEffect(() => {
    loadSecurityData();
    const interval = setInterval(loadSecurityData, 30000); // 30초마다 새로고침
    return () => clearInterval(interval);
  }, []);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      
      // Mock data
      const mockEvents: SecurityEvent[] = [
        {
          id: 'sec-1',
          timestamp: new Date().toISOString(),
          type: 'login_attempt',
          severity: 'high',
          description: 'Multiple failed login attempts from suspicious IP',
          source: 'AuthService',
          ipAddress: '192.168.1.100',
          userId: 'user-123',
          status: 'investigating',
          details: { attempts: 10, timespan: '5 minutes' }
        },
        {
          id: 'sec-2',
          timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
          type: 'system_breach',
          severity: 'critical',
          description: 'Unauthorized access attempt to admin panel',
          source: 'WebServer',
          ipAddress: '203.0.113.45',
          status: 'open',
          details: { endpoint: '/admin', method: 'POST' }
        },
        {
          id: 'sec-3',
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          type: 'permission_change',
          severity: 'medium',
          description: 'User permissions elevated to admin level',
          source: 'UserManager',
          ipAddress: '10.0.0.50',
          userId: 'admin-456',
          status: 'resolved',
          details: { targetUser: 'user-789', newRole: 'admin' }
        },
        {
          id: 'sec-4',
          timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
          type: 'data_access',
          severity: 'low',
          description: 'Large data export operation detected',
          source: 'DatabaseManager',
          ipAddress: '10.0.0.25',
          userId: 'user-321',
          status: 'resolved',
          details: { records: 10000, table: 'user_data' }
        },
        {
          id: 'sec-5',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          type: 'suspicious_activity',
          severity: 'medium',
          description: 'Unusual API usage pattern detected',
          source: 'APIGateway',
          ipAddress: '198.51.100.75',
          status: 'investigating',
          details: { requests: 1000, timespan: '1 minute' }
        }
      ];

      const mockStats: SecurityStats = {
        totalEvents: mockEvents.length,
        criticalEvents: mockEvents.filter(e => e.severity === 'critical').length,
        resolvedEvents: mockEvents.filter(e => e.status === 'resolved').length,
        activeThreats: mockEvents.filter(e => e.status === 'open' || e.status === 'investigating').length
      };

      setEvents(mockEvents);
      setStats(mockStats);
      setLoading(false);
    } catch (error) {
      console.error('보안 데이터 로딩 실패:', error);
      setLoading(false);
    }
  };

  const handleEventStatusChange = async (eventId: string, newStatus: 'resolved' | 'investigating' | 'open') => {
    try {
      setEvents(prev => prev.map(event => 
        event.id === eventId ? { ...event, status: newStatus } : event
      ));
      alert(`이벤트 상태가 ${newStatus}로 변경되었습니다.`);
    } catch (error) {
      console.error('상태 변경 실패:', error);
      alert('상태 변경에 실패했습니다.');
    }
  };

  const handleSettingsSave = async () => {
    try {
      // 실제로는 API 호출
      setShowSettingsModal(false);
      alert('보안 설정이 저장되었습니다.');
    } catch (error) {
      console.error('설정 저장 실패:', error);
      alert('설정 저장에 실패했습니다.');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
      case 'investigating': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'open': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'login_attempt': return <Key className="w-4 h-4" />;
      case 'permission_change': return <Users className="w-4 h-4" />;
      case 'data_access': return <Database className="w-4 h-4" />;
      case 'system_breach': return <AlertTriangle className="w-4 h-4" />;
      case 'suspicious_activity': return <Activity className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.ipAddress.includes(searchTerm);
    const matchesSeverity = severityFilter === 'all' || event.severity === severityFilter;
    const matchesType = typeFilter === 'all' || event.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    
    return matchesSearch && matchesSeverity && matchesType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);
  const paginatedEvents = filteredEvents.slice(
    (currentPage - 1) * eventsPerPage,
    currentPage * eventsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">보안 관리</h1>
            <p className="text-gray-600 mt-1">시스템 보안을 모니터링하고 관리하세요</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>보안 설정</span>
            </button>
            <button
              onClick={loadSecurityData}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>새로고침</span>
            </button>
          </div>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">총 이벤트</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEvents}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">위험 이벤트</p>
              <p className="text-2xl font-bold text-red-600">{stats.criticalEvents}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">해결됨</p>
              <p className="text-2xl font-bold text-green-600">{stats.resolvedEvents}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">활성 위협</p>
              <p className="text-2xl font-bold text-orange-600">{stats.activeThreats}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Activity className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="이벤트 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">모든 심각도</option>
            <option value="critical">위험</option>
            <option value="high">높음</option>
            <option value="medium">보통</option>
            <option value="low">낮음</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">모든 타입</option>
            <option value="login_attempt">로그인 시도</option>
            <option value="permission_change">권한 변경</option>
            <option value="data_access">데이터 접근</option>
            <option value="system_breach">시스템 침입</option>
            <option value="suspicious_activity">의심 활동</option>
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">모든 상태</option>
            <option value="open">열림</option>
            <option value="investigating">조사 중</option>
            <option value="resolved">해결됨</option>
          </select>
          
          <div className="text-sm text-gray-600 flex items-center">
            {filteredEvents.length}개 이벤트 표시
          </div>
        </div>
      </div>

      {/* 보안 이벤트 테이블 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">시간</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">타입</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">심각도</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">설명</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP 주소</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedEvents.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(event.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(event.type)}
                      <span className="text-sm text-gray-900 capitalize">{event.type.replace('_', ' ')}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getSeverityColor(event.severity)}`}>
                      {event.severity.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                    {event.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {event.ipAddress}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(event.status)}`}>
                      {event.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowDetailModal(true);
                        }}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg"
                        title="상세 보기"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {event.status !== 'resolved' && (
                        <button
                          onClick={() => handleEventStatusChange(event.id, 'resolved')}
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-lg"
                          title="해결됨으로 표시"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {filteredEvents.length}개 이벤트 중 {(currentPage - 1) * eventsPerPage + 1}-{Math.min(currentPage * eventsPerPage, filteredEvents.length)}개 표시
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="px-3 py-1 text-sm font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        </div>
      </div>

      {/* 이벤트 상세 모달 */}
      {showDetailModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">보안 이벤트 상세</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">시간</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedEvent.timestamp)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">타입</label>
                  <div className="flex items-center space-x-2">
                    {getTypeIcon(selectedEvent.type)}
                    <span className="text-sm text-gray-900 capitalize">{selectedEvent.type.replace('_', ' ')}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">심각도</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getSeverityColor(selectedEvent.severity)}`}>
                    {selectedEvent.severity.toUpperCase()}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">상태</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(selectedEvent.status)}`}>
                    {selectedEvent.status.toUpperCase()}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">소스</label>
                  <p className="text-sm text-gray-900">{selectedEvent.source}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">IP 주소</label>
                  <p className="text-sm font-mono text-gray-900">{selectedEvent.ipAddress}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">설명</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border">{selectedEvent.description}</p>
              </div>
              
              {selectedEvent.details && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">상세 정보</label>
                  <pre className="text-xs text-gray-900 bg-gray-50 p-3 rounded border overflow-x-auto">
                    {JSON.stringify(selectedEvent.details, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedEvent.userId && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">사용자 ID</label>
                  <p className="text-sm text-gray-900">{selectedEvent.userId}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 보안 설정 모달 */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">보안 설정</h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 비밀번호 정책 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">비밀번호 정책</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">최소 길이</label>
                    <input
                      type="number"
                      value={settings.passwordPolicy.minLength}
                      onChange={(e) => setSettings({
                        ...settings,
                        passwordPolicy: { ...settings.passwordPolicy, minLength: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div className="space-y-2">
                    {[
                      { key: 'requireUppercase', label: '대문자 필수' },
                      { key: 'requireLowercase', label: '소문자 필수' },
                      { key: 'requireNumbers', label: '숫자 필수' },
                      { key: 'requireSpecialChars', label: '특수문자 필수' }
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={settings.passwordPolicy[key as keyof typeof settings.passwordPolicy] as boolean}
                          onChange={(e) => setSettings({
                            ...settings,
                            passwordPolicy: { ...settings.passwordPolicy, [key]: e.target.checked }
                          })}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* 세션 설정 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">세션 설정</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">최대 지속시간 (분)</label>
                    <input
                      type="number"
                      value={settings.sessionSettings.maxDuration}
                      onChange={(e) => setSettings({
                        ...settings,
                        sessionSettings: { ...settings.sessionSettings, maxDuration: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">유휴 타임아웃 (분)</label>
                    <input
                      type="number"
                      value={settings.sessionSettings.idleTimeout}
                      onChange={(e) => setSettings({
                        ...settings,
                        sessionSettings: { ...settings.sessionSettings, idleTimeout: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">최대 동시 세션</label>
                    <input
                      type="number"
                      value={settings.sessionSettings.maxConcurrentSessions}
                      onChange={(e) => setSettings({
                        ...settings,
                        sessionSettings: { ...settings.sessionSettings, maxConcurrentSessions: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                </div>
              </div>
              
              {/* 접근 제어 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">접근 제어</h4>
                <div className="space-y-3">
                  {[
                    { key: 'enableTwoFactor', label: '2단계 인증 활성화' },
                    { key: 'enableIpWhitelist', label: 'IP 화이트리스트 활성화' },
                    { key: 'enableRateLimit', label: '요청 제한 활성화' }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={settings.accessControl[key as keyof typeof settings.accessControl] as boolean}
                        onChange={(e) => setSettings({
                          ...settings,
                          accessControl: { ...settings.accessControl, [key]: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">최대 로그인 시도</label>
                    <input
                      type="number"
                      value={settings.accessControl.maxLoginAttempts}
                      onChange={(e) => setSettings({
                        ...settings,
                        accessControl: { ...settings.accessControl, maxLoginAttempts: parseInt(e.target.value) }
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                </div>
              </div>
              
              {/* 모니터링 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">모니터링</h4>
                <div className="space-y-3">
                  {[
                    { key: 'enableAuditLog', label: '감사 로그 활성화' },
                    { key: 'enableRealTimeAlerts', label: '실시간 알림 활성화' }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={settings.monitoring[key as keyof typeof settings.monitoring] as boolean}
                        onChange={(e) => setSettings({
                          ...settings,
                          monitoring: { ...settings.monitoring, [key]: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">알림 이메일</label>
                    <input
                      type="email"
                      value={settings.monitoring.alertEmail}
                      onChange={(e) => setSettings({
                        ...settings,
                        monitoring: { ...settings.monitoring, alertEmail: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSettingsSave}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>저장</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityPage;